import React, {useEffect, useState, useRef, useMemo, useCallback} from 'react';
import {
  View,
  StyleSheet,
  SectionList,
  Platform,
  Linking,
  Modal,
  FlatList,
} from 'react-native';

import {withForwardedNavigationParams} from 'react-navigation-props-mapper';
import {connect, useDispatch} from 'react-redux';
import {
  NavigationProp,
  RouteProp,
  TabRouter,
  useFocusEffect,
  useRoute,
} from '@react-navigation/native';
import type {IsConnectedProps} from 'src/types/Screen';
import Localized from 'src/constants/AppStrings';
import {type RootState} from 'src/redux/store';
import Util, {getArticle, getPreviousRouteName} from 'src/Util';
import FirebaseAnalytic from 'src/nativeModules/FirebaseAnalytic';
import BaseScreen from 'src/components/screens/BaseScreen';
import Settings from 'src/Settings';
import Styles from 'src/components/Styles';
import withIsConnected from 'src/components/hoc/withIsConnected';
import RewardsTabBar, {
  TabScreen,
  useRewardsNavigation,
} from 'src/components/elements/rewards/RewardsTabBar';
import CrashlyticsEvents from 'src/logging/Crashlytics';
import Logger from 'src/logging/Logger';
import PointsIndicator from 'src/components/elements/rewards/PointsIndicator';
import RedeemPoints4Rewards from 'src/components/elements/rewards/RedeemPoints4Rewards';
import OffersErrorView from 'src/components/elements/offers/OffersErrorView';
import AVTouchableOpacity from 'src/components/elements/AVTouchableOpacity';
import CancelIcon from 'src/components/img/svg/CancelIcon';
import PayEarnPoints from 'src/components/img/svg/rewards/PayEarnPoints';
import PayExclusiveRewards from 'src/components/img/svg/rewards/PayExclusiveRewards';
import RoundedAppButton, {
  RoundedAppButtonType,
} from 'src/components/elements/RoundedAppButton';
import {getDescriber as getAccountDescriber} from 'src/components/screens/account/descriptor/DescriptorType';
import {UserRewardsTierIcon} from 'src/components/img/svg/rewards';
import {getDescriber} from './descriptor/DescriptorType';
import RewardsStatusScreen from 'src/components/elements/rewards/rewardsstatus/RewardsStatusScreens';
import AccountStore from 'src/stores/AccountStore';
import AVText from 'src/components/elements/AVText';
import {rewardStatusConfig} from 'src/components/elements/rewards/rewardsstatus/rewardStatusHelper';

import {getDescriber as getSnackDescriber} from 'src/components/screens/sendSnack/descriptor/sendasnack/DescriptorType';
import ActionsFactory from 'src/actions/ActionsFactory';
import AppEventTypes from 'src/constants/AppEventTypes';
import uuid from 'src/nativeModules/UUID';
import moment from 'moment';

import {useNavigation} from '@react-navigation/native';
import {generateErrorMessage} from 'src/logging/generateErrorMessage';
import Events from 'src/logging/Events';
import {useAppSelector} from 'src/redux/hooks';
import AccountApi from 'src/api/AccountApi';
import HttpClient from 'src/api/HttpClient';
import {
  getMaxPointValueDiff,
  getMaxRewardProductsThreshold,
} from 'src/components/elements/rewards/rewardsUtil';
import NavActions from '../../actions/NavActions';
import AppRoutes from '../../AppRoutes';
import {withGlobalize} from '../../i18n/HOC/withGlobalize';
import MoreWaysToEarnRewardScreen from 'src/components/elements/rewards/MoreWaysToEarnRewardScreen';
import MoreWaysToEarnDetailsDialogue from 'src/components/elements/rewards/MoreWaysToEarnDetailsDialogue';
import {fetchAccount} from 'src/redux/slices/accountSlice';
import {MainConsumerParamList} from 'src/types/Rewards';

type RewardsNav = NavigationProp<
  MainConsumerParamList,
  AppRoutes.RewardsScreen
>;

const lineHeightMultiplier = 1.14;
const screenWidth = Styles.ScreenWidth;
const multipler = 0.45;
const modalWidth = screenWidth * multipler;

enum SectionKey {
  Indicator = 'indicator',
  Tabs = 'tabs',
}

interface RewardsScreenProps extends IsConnectedProps {
  availablePoints: number;
  rewardProgram?: {
    [index: string]: {[index: string]: number | string};
  };
  navigation?: NavigationProp<typeof RewardsScreen>;
  refreshRewards?: boolean;
}

interface RewardsScreenState {
  availablePoints: number;
  previousRoute?: string | null;
}

const RewardsScreen = (props: RewardsScreenProps) => {
  const dispatch = useDispatch();
  const [state, _setState] = useState<RewardsScreenState>({
    availablePoints: props.availablePoints,
    previousRoute: getPreviousRouteName(props.navigation?.getState()?.routes),
  });
  const _navigation = useNavigation<RewardsNav>();
  const sectionListRef = useRef(null);
  const isNewUi = Settings.isNewUI();
  const account = useAppSelector((s) => s.account.account);
  const [showFirstVisitRewardMdl, setShowFirstVisitRewardMdl] = useState(false);
  const [isConnected, setIsConnected] = useState<boolean>(true);
  const {rewardTier, rewardStatuses} = AccountStore.getRewardStatus() || {};
  const {rewardProducts} = AccountStore.getRewardProducts() || {};
  const [isScreenReady, setIsScreenReady] = useState(false);
  const productThresholds =
    rewardProducts?.map((product) => product.pointsRequired) ?? [];
  const [refreshKey, setRefreshKey] = React.useState(0);
  const route =
    useRoute<RouteProp<MainConsumerParamList, AppRoutes.RewardsScreen>>();

  const suppressRedirect = route.params?.suppressRedirect;
  const refreshRewards = props.refreshRewards ?? route.params?.refreshRewards;

  useEffect(() => {
    setIsConnected(props.isConnected);
  }, [props.isConnected]);

  useEffect(() => {
    FirebaseAnalytic.trackEvent('componentDidMount', 'RewardsScreen', {
      ...props,
      ...state,
    });
  }, []);

  const refreshRewardsFn = async () => {
    try {
      const accountId = AccountStore.getAccountId();
      await Promise.all([
        ActionsFactory.getAccountActions().loadRewardStatus(accountId),
        dispatch(fetchAccount(accountId)),
      ]);
      setRefreshKey((k) => k + 1);
    } catch (e) {
      Logger.Log.LogAPIEvent(
        'RewardsScreen',
        'refreshRewards',
        '{}',
        JSON.stringify(e),
      );
    }
  };

  const generateTabs = useMemo(() => {
    const tabs = [
      <TabScreen
        name={Localized.Labels.my_rewards}
        component={RedeemPoints4Rewards}
        listeners={{
          tabPress: () => {
            scrollToNextSection(0);
          },
        }}
        key={Localized.Labels.my_rewards}
      />,
      <TabScreen
        name={Localized.Labels.my_status}
        component={RewardsStatusScreen}
        listeners={{
          tabPress: () => {
            scrollToNextSection(1);
          },
        }}
        key={Localized.Labels.my_status}
      />,
    ];
    const moreWaysToEarn = AccountStore.getRewardMoreWaysToEarn();
    if (moreWaysToEarn && moreWaysToEarn?.length > 0) {
      tabs.splice(
        1,
        0,
        <TabScreen
          name={Localized.Labels.more_ways_to_earn}
          component={MoreWaysToEarnRewardScreen}
          listeners={{
            tabPress: () => {
              scrollToNextSection(0);
            },
          }}
          key={`mwe-${refreshKey}`}
        />,
      );
    }
    return tabs;
  }, [refreshKey]);

  /**
   * Renders a view indicating no network connectivity.
   *
   * This function displays an error view with a message for network issues and provides
   * a button for the user to navigate to the account settings to resolve the connectivity problem.
   *
   * @returns {JSX.Element} A styled view with an error message and a navigation button.
   */
  const renderNoConnectivity = () => (
    <View style={[styles.noOffersView]}>
      <OffersErrorView
        errorText={Localized.Errors.error_rewards_connection}
        buttonLabel={Localized.Buttons.go_to_settings_receipt}
        onPress={() => Linking.openSettings()}
      />
    </View>
  );
  const {
    state: tabState,
    navigation,
    descriptors,
    NavigationContent,
  } = useRewardsNavigation(TabRouter, {
    children: generateTabs,
  });

  useFocusEffect(
    useCallback(() => {
      if (refreshRewards) {
        (async () => {
          await refreshRewardsFn();

          if (!suppressRedirect) {
            navigation.navigate(Localized.Labels.more_ways_to_earn);
          }

          _navigation.setParams?.({
            refreshRewards: false,
            suppressRedirect: false,
          });
        })();
      }
    }, [_navigation, navigation, refreshRewards, suppressRedirect]),
  );

  useEffect(() => {
    const loadScreen = async () => {
      await getAppRewardsDetails(); // or any other async tasks
      setIsScreenReady(true);
    };

    props.isConnected && loadScreen();
  }, []);

  const getAppRewardsDetails = async () => {
    let rewardsVersion = account.rewardTour;
    if (!rewardsVersion || rewardsVersion === '') {
      let res;
      try {
        res = await AccountApi.fetchAccount(AccountStore.getAccountId());
        rewardsVersion = res?.rewardTour;
        Logger.Log.LogAPIEvent(
          'AccountAPI',
          'FetchAccount',
          JSON.stringify({accountId: AccountStore.getAccountId()}),
          JSON.stringify(res),
        );
      } catch (err) {
        Logger.Log.LogAPIEvent(
          'AccountAPI',
          'FetchAccount',
          JSON.stringify({accountId: AccountStore.getAccountId()}),
          JSON.stringify(err),
        );
      }
    }
    const appRewardsData = HttpClient.parseJsonSafe(rewardsVersion);
    if (appRewardsData?.AppRewardsShown === 'Viewed') {
      setShowFirstVisitRewardMdl(false);
    } else {
      setShowFirstVisitRewardMdl(true);
    }
  };

  const updateAppRewardsEvent = async () => {
    try {
      const appRewardsShown = JSON.stringify({
        AppRewardsShown: 'Viewed',
      });
      const appRewardsResponse =
        await ActionsFactory.getAccountActions().updateAppEvent(
          AccountStore.getAccountId(),
          AppEventTypes.RewardTour,
          Util.getCurrentUTCDate(),
          Util.getCurrentDate(),
          moment.tz.guess(),
          appRewardsShown,
        );
      Logger.Log.LogAPIEvent(
        'AppAPI',
        'UpdateAppEvent',
        JSON.stringify({
          accountId: AccountStore.getAccountId(),
          AppEvent: AppEventTypes.RewardTour,
          appRewardsShown,
        }),
        JSON.stringify(appRewardsResponse),
      );
      hideOneTimeModal();
    } catch (error) {
      const guid = await uuid.getRandomUUID();
      CrashlyticsEvents.log(
        'Exception',
        'AppRewardsScreen:UpdateAppTourEvent',
        generateErrorMessage(error),
        guid,
      );
      Events.Error.trackEvent(
        'Exception',
        'AppRewardsScreen:UpdateAppTourEvent',
        generateErrorMessage(error),
        guid,
      );
      Logger.Log.LogAPIEvent(
        'AppAPI',
        'UpdateAppEvent',
        JSON.stringify({
          accountId: AccountStore.getAccountId(),
          AppEvent: AppEventTypes.RewardTour,
        }),
        JSON.stringify(error),
      );
    }
  };
  const hideOneTimeModal = () => {
    setShowFirstVisitRewardMdl(false);
  };

  const renderStatusItem = ({item}) => (
    <View style={styles.itemContainer}>
      <View style={styles.iconContainer}>
        <UserRewardsTierIcon
          tierName={item.name}
          size={Styles.Heights.webRewardImageHeight}
        />
      </View>
      <View style={styles.textContainer}>
        <AVText
          style={[
            styles.tierName,
            getSnackDescriber()?.snackDetailsBtnFontSTyle(),
          ]}
          aria-label={`${item.name} text`}
          accessibilityLabel={item.name}
        >
          {item.name}
        </AVText>

        <AVText
          style={[
            styles.pointsRate,
            getSnackDescriber()?.snackDetailsBtnFontSTyle(),
            styles.description,
          ]}
          accessibilityLabel={`$1 = ${item.pointsRate} points`}
          aria-label={`$1 = ${item.pointsRate} points`}
        >
          {`$1 = ${item.pointsRate} points`}
        </AVText>
      </View>
    </View>
  );

  const renderOneTimeModal = () => {
    const val = Localized.Labels.formatString(
      Localized.Labels.earn_points_desc,
      rewardTier?.tierName,
      rewardTier?.pointsRate,
      getArticle(rewardTier?.tierName),
    );
    return (
      <Modal visible={true} onDismiss={hideOneTimeModal} transparent={true}>
        <View style={styles.oneTimeRewardModalMain}>
          <View style={styles.oneTimeRewardModalContainer}>
            <AVTouchableOpacity
              aria-label={`X icon, Double tap to ${Localized.Buttons.cancel} offer details`}
              role="button"
              style={styles.closeModalTitleContainer}
              onPress={updateAppRewardsEvent}
              accessible
              accessibilityLabel={Localized.Buttons.cancel}
              accessibilityRole="button"
              hitSlop={Styles.HitSlop_10}
            >
              <CancelIcon />
            </AVTouchableOpacity>

            <View style={styles.oneTimeRewardModalContentContainer}>
              <AVText
                style={[
                  styles.oneTimerewardsTitle,
                  getSnackDescriber()?.snackDetailsBtnFontSTyle(),
                ]}
                accessibilityLabel={getDescriber().getOneTimeTitle()}
                aria-label={`${getDescriber().getOneTimeTitle()} text`}
              >
                {getDescriber().getOneTimeTitle()}
              </AVText>
              <View style={styles.section}>
                <View style={styles.leftContent}>
                  <View style={styles.leftsection}>
                    <PayEarnPoints
                      color={getDescriber().getOneTimePayEarnColor()}
                    />
                  </View>
                  <AVText
                    accessibilityLabel={Localized.Labels.earn_points}
                    aria-label={`${Localized.Labels.earn_points} text`}
                    style={[
                      styles.earnPntsTxt,
                      getSnackDescriber()?.snackDetailsBtnFontSTyle(),
                    ]}
                  >
                    {Localized.Labels.earn_points}
                  </AVText>
                  <AVText
                    accessibilityLabel={val.toString()}
                    aria-label={`${val.toString()} text`}
                    style={[
                      styles.earn_pts_desc,
                      getSnackDescriber()?.snackDetailsBtnFontSTyle(),
                    ]}
                  >
                    {Localized.Labels.formatString(
                      Localized.Labels.earn_points_desc,
                      rewardTier?.tierName,
                      rewardTier?.pointsRate,
                      getArticle(rewardTier?.tierName),
                    )}
                  </AVText>
                </View>

                {/* Right Content */}
                <View style={styles.rightContent}>
                  <View style={styles.rightsubsec}>
                    <PayExclusiveRewards
                      color={getDescriber().getOneTimePayEarnColor()}
                    />
                  </View>
                  <AVText
                    accessibilityLabel={Localized.Labels.exclusive_rewards}
                    aria-label={`${Localized.Labels.exclusive_rewards} text`}
                    style={styles.exclusive_rwds_lbl}
                  >
                    {Localized.Labels.exclusive_rewards}
                  </AVText>
                  <AVText
                    accessibilityLabel={Localized.Labels.exclusive_rew_desc}
                    aria-label={`${Localized.Labels.exclusive_rew_desc} text`}
                    style={[
                      styles.earn_pts_desc,
                      getSnackDescriber()?.snackDetailsBtnFontSTyle(),
                    ]}
                  >
                    {Localized.Labels.exclusive_rew_desc}
                  </AVText>
                </View>
              </View>
              <View style={styles.line} />
              <AVText
                accessibilityLabel={getDescriber().getStatusSystem()}
                aria-label={`${getDescriber().getStatusSystem()} text`}
                style={[
                  styles.sttusSystem,
                  getSnackDescriber()?.snackDetailsBtnFontSTyle(),
                ]}
              >
                {getDescriber().getStatusSystem()}
              </AVText>
              <AVText
                accessibilityLabel={Localized.Labels.pay_status_system_desc}
                aria-label={`${Localized.Labels.pay_status_system_desc} text`}
                style={[
                  styles.paystatusSystemDesc,
                  getSnackDescriber()?.snackDetailsBtnFontSTyle(),
                ]}
              >
                {Localized.Labels.pay_status_system_desc}
              </AVText>

              <FlatList
                data={rewardStatuses} // The array of objects
                keyExtractor={(item, index) => index + item.tierName} // Unique key for each item
                renderItem={renderStatusItem} // Render function
                style={styles.flatlistPadding}
                numColumns={2}
              />
              <View style={styles.letsGoBtn}>
                <RoundedAppButton
                  buttonType={RoundedAppButtonType.Solid}
                  maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm8}
                  accessibilityLabelValue={Localized.Buttons.lets_go}
                  aria-label={Localized.Buttons.lets_go}
                  onPress={updateAppRewardsEvent}
                  buttonViewStyle={{paddingHorizontal: 24}}
                  buttonContainerStyle={[
                    {
                      paddingHorizontal: Styles.Spacing.m2 + 4,
                    },
                    getAccountDescriber()?.getStyleDescriptor().buttonSpacing,
                  ]}
                  textStyle={[
                    Styles.Style.actionButtonText,
                    getDescriber().getLetsGoStyle(),
                  ]}
                  titleText={Localized.Buttons.lets_go}
                />
              </View>
            </View>
          </View>
        </View>
      </Modal>
    );
  };

  const scrollToNextSection = (index) => {
    sectionListRef.current.scrollToLocation({
      animated: true,
      itemIndex: index,
      sectionIndex: index,
    });
  };

  const getMaxPointLabel = () => {
    return getMaxRewardProductsThreshold(rewardProducts) + '+';
  };

  const maxPointsLabel = getMaxPointLabel();
  const maxPointValue = getMaxPointValueDiff(productThresholds, rewardProducts);
  const getRewardPoints = () => {
    if (account.rewardPrograms?.length > 0) {
      return account.rewardPrograms[0]?.rewardPoints ?? 0;
    } else {
      return 0;
    }
  };
  return (
    <BaseScreen
      onBackSelect={() => {
        if (
          NavActions.navigatorRef.current?.getCurrentRoute()?.name ===
          'My Rewards'
        ) {
          NavActions.navigate(AppRoutes.Promotions);
        }
      }}
      title={Localized.Labels.rewards}
      previousRoute={state.previousRoute}
      accessibilityLabel={isNewUi ? Localized.Labels.rewards : 'Back arrow'}
      accessibilityHint={
        isNewUi
          ? Localized.Labels.rewards
          : `Press to navigate back to the ${state.previousRoute} screen`
      }
    >
      {!isConnected && !isScreenReady && renderNoConnectivity()}
      {(isConnected || isScreenReady) && (
        <>
          <NavigationContent key={`nav-${refreshKey}`}>
            <SectionList
              ref={sectionListRef}
              nestedScrollEnabled
              contentContainerStyle={styles.content}
              stickySectionHeadersEnabled={Platform.OS !== 'web'}
              sections={[
                {key: SectionKey.Indicator, data: []},
                {key: SectionKey.Tabs, data: [1]},
              ]}
              renderSectionHeader={({section: {key}}) => {
                if (key === SectionKey.Indicator) {
                  return (
                    <>
                      <PointsIndicator
                        availablePoints={getRewardPoints()}
                        thresholds={productThresholds}
                        maxPointValue={maxPointValue}
                        tierName={rewardTier?.tierName}
                        rewardedColor={
                          rewardStatusConfig.circlePointsIndicatorFillColor
                        }
                        maxPointValueLabel={maxPointsLabel}
                      />
                      <AVText> </AVText>
                      <View style={styles.divider} />
                    </>
                  );
                }

                if (key === SectionKey.Tabs) {
                  return (
                    <RewardsTabBar
                      state={tabState}
                      descriptors={descriptors}
                      navigation={navigation}
                    />
                  );
                }

                return null;
              }}
              renderItem={({item}) =>
                item > 0 && (
                  <>
                    {tabState.routes.map((route, i) => {
                      return (
                        <View
                          key={route.key}
                          style={{
                            display: i === tabState.index ? 'flex' : 'none',
                          }}
                        >
                          {descriptors[route.key].render()}
                        </View>
                      );
                    })}
                  </>
                )
              }
              extraData={refreshKey}
            />
          </NavigationContent>
          {showFirstVisitRewardMdl && renderOneTimeModal()}
          <MoreWaysToEarnDetailsDialogue
            exploreNowAction={() => {
              navigation.navigate(Localized.Labels.more_ways_to_earn);
            }}
          />
        </>
      )}
    </BaseScreen>
  );
};

const styles = StyleSheet.create({
  container: {
    marginTop: Styles.Spacing.m3,
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  divider: {
    backgroundColor: Styles.neutralHues,
    height: StyleSheet.hairlineWidth,
    width: '100%',
    marginTop: Styles.Spacing.m3,
    paddingBottom: 1,
  },
  content: {
    paddingVertical: Styles.Spacing.m3,
  },
  noOffersView: {
    flex: 1,
    alignItems: 'center',
    marginTop: 120,
  },
  oneTimeRewardModalMain: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  oneTimeRewardModalContainer: {
    width: modalWidth,
    backgroundColor: Styles.white,
    borderRadius: Styles.Spacing.m3,
    paddingHorizontal: Styles.Spacing.m4, // Padding inside the container
    paddingVertical: Styles.Spacing.m4 - Styles.Spacing.m2, // Padding inside the container
  },
  oneTimeRewardModalContentContainer: {
    paddingTop: 10,
  },
  closeModalTitleContainer: {
    zIndex: 999999,
    position: 'absolute',
    top: 5,
    right: 5,
    width: 48,
    height: 48,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 24,
  },
  leftContent: {
    flex: 0.5, // Takes equal width as the right content
    alignItems: 'center', // Centers content horizontally within leftContent
    justifyContent: 'flex-start', // Starts from the top vertically
    paddingRight: Styles.Spacing.m2 + 2,
    marginTop: Styles.Spacing.m2 + Styles.Spacing.m3,
  },
  rightContent: {
    flex: 0.5, // Takes equal width as the left content
    alignItems: 'center', // Centers content horizontally within rightContent
    justifyContent: 'flex-start', // Starts from the top vertically
    marginTop: Styles.Spacing.m2 + Styles.Spacing.m3,
  },
  rightsubsec: {
    alignSelf: 'center', // Center icon
    marginBottom: 8, // Space between the icon and title
  },

  itemContainer: {
    flexDirection: 'row', // Align items horizontally
    alignItems: 'flex-start', // Align items to the top
    marginTop: Styles.Spacing.m4,
    width: '50%',
  },
  iconContainer: {
    marginRight: Styles.Spacing.m2, // Space between icon and text
  },
  textContainer: {
    flexDirection: 'column', // Stack title and description vertically
    alignSelf: 'center',
    justifyContent: 'center',
  },

  description: {
    marginTop: 5,
  },
  tierName: {
    fontSize: Styles.Fonts.f1 + 2,
    lineHeight: (Styles.Fonts.f1 + 2) * lineHeightMultiplier,
    fontWeight: '700',
  },
  pointsRate: {
    fontSize: Styles.Fonts.f1,
    lineHeight: Styles.Fonts.f1 * lineHeightMultiplier,
    fontWeight: '400',
    color: '#707070',
  },
  oneTimerewardsTitle: {
    fontSize: Styles.Fonts.f1 + 6,
    lineHeight: (Styles.Fonts.f1 + 6) * lineHeightMultiplier,
    fontWeight: '700',
    paddingBottom: 16,
  },
  section: {
    width: '100%',
    paddingBottom: Styles.Fonts.f0 - 2,

    flexDirection: 'row', // Row layout for side-by-side
    justifyContent: 'center', // Centers the content horizontally
    alignItems: 'center', // Centers the content vertically
  },
  leftsection: {
    alignSelf: 'center', // Center icon
    marginBottom: Styles.Spacing.m2, // Space between the icon and title
  },
  earnPntsTxt: {
    textAlign: 'center', // Center the title
    marginBottom: 4, // Space between title and description

    fontSize: Styles.Fonts.f1,
    lineHeight: Styles.Fonts.f1 * lineHeightMultiplier,
    fontWeight: '700',
  },
  earn_pts_desc: {
    textAlign: 'center',
    fontSize: Styles.Fonts.f1,
    lineHeight: Styles.Fonts.f1 * lineHeightMultiplier,
    fontWeight: '400',
  },
  sttusSystem: {
    marginBottom: 4,
    fontSize: Styles.Fonts.f1 + 2,
    lineHeight: (Styles.Fonts.f1 + 2) * lineHeightMultiplier,
    fontWeight: '700',
  },
  paystatusSystemDesc: {
    marginBottom: Styles.Spacing.m1,
    fontSize: Styles.Fonts.f1,
    lineHeight: Styles.Fonts.f1 * lineHeightMultiplier,
    fontWeight: '400',
  },
  line: {
    height: 1,
    width: '100%',
    backgroundColor: '#CFCFCF',
    marginVertical: Styles.Spacing.m4,
    alignContent: 'center',
    alignSelf: 'center',
  },
  exclusive_rwds_lbl: {
    textAlign: 'center', // Center the title
    marginBottom: 4, // Space between title and description
    fontSize: Styles.Fonts.f1,
    lineHeight: Styles.Fonts.f1 * lineHeightMultiplier,
    fontWeight: '700',
  },
  letsGoBtn: {
    alignSelf: 'center',
    marginTop: Platform.OS === 'web' ? Styles.Spacing.m4 : Styles.Spacing.m0,
  },
  flatlistPadding: {paddingVertical: Styles.Spacing.m2 + 2},
});

const ConnectedRewardsScreen = connect((state: RootState) => ({
  availablePoints: state.account.account.points,
  rewardProgram: state.account.account.rewardPrograms,
}))(RewardsScreen);

export default withForwardedNavigationParams()(
  withGlobalize(withIsConnected(ConnectedRewardsScreen)),
);
